import { defineStore } from "pinia";
import type { DataValue, TimeFrame } from "@/types.ts";
import { StoreModule } from "@/constants/store.ts";
import api from "@/_helpers/api.ts";
import { axiosInstance } from "@/plugins/https.ts";
import { type TicketTrigger, TicketType } from "@/constants/tickets.ts";
import { SubscriptionModule } from "@/constants/workplaces.ts";
import { convertTimeFrameToUnix } from "@/_helpers/date";
import { i18n } from "@/plugins/i18n.ts";
import {
  getCurrentDate,
  getNDaysAgo,
  getNinetyDaysAgo,
  type PresetDate,
} from "@/constants/date-range-picker.ts";

export const getDefaultCoroImpactDateRange = (): TimeFrame => ({
  start: getNDaysAgo(30),
  end: getCurrentDate(),
});

export const getDefaultCoroImpactDateRangePresets = (): PresetDate[] => [
  {
    label: i18n.global.t("dateRangePicker.lastThirtyDays"),
    value: [getNDaysAgo(30), getCurrentDate()],
  },
  {
    label: i18n.global.t("dateRangePicker.lastNinetyDays"),
    value: [getNinetyDaysAgo(), getCurrentDate()],
  },
];

export interface CoroImpactSpotlightUser {
  displayName: string;
  email: string;
  count: number;
  workspaceName?: string;
  workspaceId?: string;
}

export interface TicketsSpotlightWidgetDataItem {
  eventTrigger: TicketTrigger;
  eventType: TicketType;
  count: number;
  percentile: number;
  workspacesCount?: number;
}

export interface TicketsSpotlightWidgetData {
  total: number;
  delta: number;
  eventTriggers: TicketsSpotlightWidgetDataItem[];
}

export interface AutoResolvedWidgetDataItem {
  name: SubscriptionModule;
  count: number;
}

export interface AutoResolvedWidgetData {
  total: number;
  delta: number;
  modules: AutoResolvedWidgetDataItem[];
}

export interface CoroImpactState {
  selectedDateRange: TimeFrame;
  topVulnerableUsers: DataValue<CoroImpactSpotlightUser[]>;
  topViolatorUsers: DataValue<CoroImpactSpotlightUser[]>;
  topSuspiciousEmailSources: DataValue<TopSuspiciousEmailSource[]>;
  autoResolvedData: DataValue<AutoResolvedWidgetData>;
  ticketsSpotlightData: DataValue<TicketsSpotlightWidgetData>;
}

export interface TopSuspiciousEmailSource {
  name: string;
  count: number;
  delta: number; // e.g., 23 for +23%, -24 for -24%
}

export const useCoroImpactStore = defineStore(StoreModule.CORO_IMPACT, {
  state: (): CoroImpactState => ({
    selectedDateRange: getDefaultCoroImpactDateRange(),
    topVulnerableUsers: { loading: false },
    topViolatorUsers: { loading: false },
    topSuspiciousEmailSources: { loading: false },
    autoResolvedData: { loading: false },
    ticketsSpotlightData: { loading: false },
  }),

  getters: {
    unixTimeFrame: (state: CoroImpactState) => {
      return convertTimeFrameToUnix(state.selectedDateRange);
    },
  },

  actions: {
    async getTopVulnerableUsers() {
      try {
        this.topVulnerableUsers = { loading: true };
        const request = {
          ...api.coroImpactVulnerableUsers(),
          params: { ...this.unixTimeFrame },
        };
        const { data } = await axiosInstance.request<CoroImpactSpotlightUser[]>(request);
        this.topVulnerableUsers.data = data;
      } catch (error) {
        this.topVulnerableUsers.error = true;
        console.error("Error fetching top vulnerable users:", error);
      } finally {
        this.topVulnerableUsers.loading = false;
      }
    },

    async getTopViolatorUsers() {
      try {
        this.topViolatorUsers = { loading: true };
        const request = {
          ...api.coroImpactViolatorUsers(),
          params: { ...this.unixTimeFrame },
        };
        const { data } = await axiosInstance.request<CoroImpactSpotlightUser[]>(request);
        this.topViolatorUsers.data = data;
      } catch (error) {
        this.topViolatorUsers.error = true;
        console.error("Error fetching top violator users:", error);
      } finally {
        this.topViolatorUsers.loading = false;
      }
    },

    async getAutoResolvedWidgetData() {
      try {
        this.autoResolvedData = { loading: true };
        const request = {
          ...api.coroImpactAutoResolvedTickets(),
          params: { ...this.unixTimeFrame },
        };
        const { data } = await axiosInstance.request<AutoResolvedWidgetData>(request);
        this.autoResolvedData.data = data;
      } catch (error) {
        this.autoResolvedData.error = true;
        console.error("Error fetching auto resolved tickets widget data:", error);
      } finally {
        this.autoResolvedData.loading = false;
      }
    },

    async getTopSuspiciousEmailSourcesWidgetData() {
      try {
        this.topSuspiciousEmailSources = { loading: true };
        const request = {
          ...api.coroImpactSuspiciousDomains(),
          params: { ...this.unixTimeFrame },
        };
        const { data } = await axiosInstance.request<TopSuspiciousEmailSource[]>(request);
        this.topSuspiciousEmailSources.data = data;
      } catch (error) {
        this.topSuspiciousEmailSources.error = true;
        console.error("Error fetching top suspicious email sources:", error);
      } finally {
        this.topSuspiciousEmailSources.loading = false;
      }
    },

    async getTicketsSpotlightWidgetData() {
      try {
        this.ticketsSpotlightData = { loading: true };
        const request = {
          ...api.coroImpactTickets(),
          params: { ...this.unixTimeFrame },
        };
        const { data } = await axiosInstance.request<TicketsSpotlightWidgetData>(request);
        this.ticketsSpotlightData.data = data;
      } catch (error) {
        this.ticketsSpotlightData.error = true;
        console.error("Error fetching tickets spotlight widget data:", error);
      } finally {
        this.ticketsSpotlightData.loading = false;
      }
    },
  },
});
